// ============================================================
// DEPLOYMENT — unit placement & path drawing
// ============================================================
//
// 一個 game mode 啟動時，Game 端會呼叫 `deployment.setRules(...)`
// 套上一份 `DeploymentRules`：
//   - manual : 玩家自己擺位置 + 畫路徑（Single / PvP）。Palette 顯示，
//              有 composition cap。`placeUnit` / `loadFromUnits` 都
//              用 `rules.composition` 限制兵力上限。
//   - preset : 陣型由腳本決定（Story / Casual）。Palette 隱藏，
//              `placeUnit` 拒收新單位，`loadFromUnits` 信任腳本不
//              套 cap。玩家只能畫路徑。
//
// `rules` 是「佈陣 UI 行為與兵力上限的單一信任來源」。Renderer / Input
// 看 `rules.kind` 決定要不要畫 palette / save slot row / Remove 按鈕，
// ArmyData 也讀 `rules.composition` 做存檔 cap 驗證（caller 傳進來）。
//
// 規則型別本身定義在 `balance.ts §10`。
// ============================================================

import { BATTLE_COLS, BATTLE_ROWS, UCFG, BASES } from './constants.ts';
import {
  type DeploymentRules,
  DEFAULT_MANUAL_RULES,
  AI_MAX_PLACEMENT_ATTEMPTS,
  AI_MIN_PATH_STEPS,
  AI_ROW_RANGE,
} from './balance.ts';
import { BattleUnit, Pos, mkUnit } from './BattleUnit.ts';
import { ri } from './utils.ts';
import { SeededRng } from './server/rng.ts';

/** A unit placed on the board during deployment */
export interface DeployedUnit {
  type: string;
  pos: Pos;
  path: Pos[];
}

/** Unit type available in the palette with remaining count */
export interface PaletteSlot {
  type: string;
  max: number;
  placed: number;
}

/** Base tile positions (not placeable) */
const BASE_TILES = new Set<string>();
for (const b of BASES) {
  for (const t of b.tiles) BASE_TILES.add(`${t.x},${t.y}`);
}

/** Player placeable rows: bottom 2 rows (rows 6-7), excluding base tiles */
const PLAYER_MIN_ROW = 6;
const PLAYER_MAX_ROW = 7;

/** AI placeable rows (from balance.ts AI_ROW_RANGE). */
const AI_MIN_ROW = AI_ROW_RANGE[0];
const AI_MAX_ROW = AI_ROW_RANGE[1];

export class Deployment {
  /** Units placed by the player */
  placed: DeployedUnit[] = [];

  /**
   * Active deployment rules — single source of truth for:
   *   - palette composition + cap (manual)
   *   - UI mode (manual = show palette + save slots; preset = hide them)
   *   - `placeUnit` / `loadFromUnits` cap behaviour
   * Initialised to `DEFAULT_MANUAL_RULES`; Game's `start*` entries call
   * `setRules()` at mode boot to switch to the correct rule.
   */
  rules: DeploymentRules = DEFAULT_MANUAL_RULES;

  /**
   * Unit palette — derived from `rules.composition` when `rules.kind === 'manual'`,
   * empty array in preset mode. Rebuilt by `setRules()`. Never mutated from outside.
   */
  palette: PaletteSlot[] = this._buildPalette(DEFAULT_MANUAL_RULES);

  /** Currently selected palette slot index (-1 = none) */
  selectedPalette = -1;
  /** Currently selected placed unit index (-1 = none).
   *  In pathMode ON: target for path drawing.
   *  In pathMode OFF: visually highlighted; enables the Remove button. */
  selectedUnit = -1;
  /** Path-draw mode toggle (no screen transition).
   *  ON  → tap a unit to plan / re-plan its path; drag from it to draw.
   *  OFF → drag a placed unit to reposition it on the grid, or to the
   *        bottom panel to remove it. */
  pathMode = false;
  /** Dragging state (palette → grid) */
  dragging = false;
  dragType = '';
  dragPos: Pos | null = null;
  /** Dragging a placed unit from the grid (only in pathMode OFF).
   *  Drop target decides intent:
   *  - bottom zone        → remove
   *  - empty player cell  → reposition (path is cleared)
   *  - origin / invalid   → cancel (no-op) */
  dragRemoveIdx = -1;
  /**
   * Hook fired after any mutation to `placed` (place / remove / path edits).
   * Used by Game.ts to trigger autosave. Set externally; kept optional so tests
   * and isolated usage don't need to wire it up.
   */
  onChange: (() => void) | undefined = undefined;

  // ----------------------------------------------------------
  // Rules
  // ----------------------------------------------------------

  /**
   * Install a new rule set. Resets `placed`, palette counters, selection,
   * drag state, and pathMode. Does NOT fire `onChange` (callers usually
   * follow with `loadFromUnits` if they have preset content; autosave
   * shouldn't fire on the empty reset).
   *
   * Idempotent — safe to call repeatedly with the same rules.
   */
  setRules(rules: DeploymentRules): void {
    this.rules = rules;
    this.placed = [];
    this.palette = this._buildPalette(rules);
    this.selectedPalette = -1;
    this.selectedUnit = -1;
    this.pathMode = false;
    this.dragging = false;
    this.dragType = '';
    this.dragPos = null;
    this.dragRemoveIdx = -1;
    // Clear any prior autosave hook — each mode re-attaches its own.
    this.onChange = undefined;
  }

  private _buildPalette(rules: DeploymentRules): PaletteSlot[] {
    if (rules.kind !== 'manual') return [];
    return rules.composition.map(([type, max]) => ({ type, max, placed: 0 }));
  }

  /** Invoke onChange safely — never throw into the caller */
  private _emit(): void {
    if (!this.onChange) return;
    try { this.onChange(); } catch (e) {
      console.warn('[Deployment] onChange hook threw', e);
    }
  }

  // ----------------------------------------------------------
  // Grid / cell queries
  // ----------------------------------------------------------

  /** Check if a grid cell is valid for player placement */
  isPlayerCell(x: number, y: number): boolean {
    if (x < 0 || x >= BATTLE_COLS || y < PLAYER_MIN_ROW || y > PLAYER_MAX_ROW) return false;
    if (BASE_TILES.has(`${x},${y}`)) return false;
    return true;
  }

  /** Check if a cell is already occupied by a placed unit */
  isOccupied(x: number, y: number): boolean {
    return this.placed.some(u => u.pos.x === x && u.pos.y === y);
  }

  /** Find placed unit at grid position */
  unitAt(x: number, y: number): number {
    return this.placed.findIndex(u => u.pos.x === x && u.pos.y === y);
  }

  // ----------------------------------------------------------
  // Manual placement (manual mode only)
  // ----------------------------------------------------------

  /**
   * Place a unit from the palette onto the grid. No-op in preset mode
   * (units come from the level/chapter script, player can't add).
   */
  placeUnit(type: string, x: number, y: number): boolean {
    if (this.rules.kind !== 'manual') return false;
    if (!this.isPlayerCell(x, y)) return false;
    if (this.isOccupied(x, y)) return false;
    const slot = this.palette.find(s => s.type === type);
    if (!slot || slot.placed >= slot.max) return false;
    this.placed.push({ type, pos: { x, y }, path: [] });
    slot.placed++;
    this._emit();
    return true;
  }

  /** Remove a placed unit and return it to the palette. No-op in preset mode. */
  removeUnit(index: number): void {
    if (this.rules.kind !== 'manual') return;
    if (index < 0 || index >= this.placed.length) return;
    const u = this.placed[index];
    const slot = this.palette.find(s => s.type === u.type);
    if (slot) slot.placed--;
    this.placed.splice(index, 1);
    if (this.selectedUnit >= this.placed.length) this.selectedUnit = -1;
    this._emit();
  }

  /** Move a placed unit to a different cell. No-op in preset mode (locked layout).
   *  Returns true on success. Clears the unit's path on success because
   *  paths are absolute coordinates relative to the unit's placement. */
  moveUnit(index: number, x: number, y: number): boolean {
    if (this.rules.kind !== 'manual') return false;
    if (index < 0 || index >= this.placed.length) return false;
    if (!this.isPlayerCell(x, y)) return false;
    const u = this.placed[index];
    if (u.pos.x === x && u.pos.y === y) return false; // no-op
    if (this.isOccupied(x, y)) return false;
    u.pos = { x, y };
    u.path = [];
    this._emit();
    return true;
  }

  // ----------------------------------------------------------
  // Path editing (manual + preset)
  // ----------------------------------------------------------

  /** Flip path-draw mode. Cancels any in-flight drag state.
   *  Selection is preserved across mode switches. */
  togglePathMode(): void {
    this.pathMode = !this.pathMode;
    this.dragRemoveIdx = -1;
    this.dragPos = null;
  }

  /** Add a waypoint to the selected unit's path */
  addPathPoint(x: number, y: number): boolean {
    if (this.selectedUnit < 0) return false;
    const u = this.placed[this.selectedUnit];
    const cfg = UCFG[u.type];
    if (u.path.length >= cfg.maxPath) return false;
    if (x < 0 || x >= BATTLE_COLS || y < 0 || y >= BATTLE_ROWS) return false;
    if (BASE_TILES.has(`${x},${y}`)) return false;

    // Must be adjacent (Manhattan distance 1) to last point in path or unit position
    const last = u.path.length > 0 ? u.path[u.path.length - 1] : u.pos;
    const dx = Math.abs(x - last.x), dy = Math.abs(y - last.y);
    if (dx + dy !== 1) return false;

    // Don't allow duplicate consecutive points
    if (x === last.x && y === last.y) return false;

    u.path.push({ x, y });
    this._emit();
    return true;
  }

  /** Undo the last path point for the selected unit */
  undoPathPoint(): void {
    if (this.selectedUnit < 0) return;
    this.placed[this.selectedUnit].path.pop();
    this._emit();
  }

  /** Clear path for the selected unit */
  clearPath(): void {
    if (this.selectedUnit < 0) return;
    this.placed[this.selectedUnit].path = [];
    this._emit();
  }

  // ----------------------------------------------------------
  // Bulk load
  // ----------------------------------------------------------

  /**
   * Replace current deployment with the given unit list.
   * Resets selection / drag state and fires `onChange` once at end.
   *
   * Cap behaviour depends on `rules.kind`:
   *   - manual : `composition` cap enforced — units past the per-type
   *              max are silently dropped. Defends against corrupted
   *              localStorage or mismatched-mode loads.
   *   - preset : no cap — trusts the caller (scripted level data is the
   *              source of truth). Palette counter still bumps (for UI
   *              feedback) but isn't gated.
   *
   * Caller responsibilities:
   *   - manual: validate structural integrity via ArmyData.validatePayload
   *     before passing in (positions in bounds, paths Manhattan-adjacent, etc).
   *   - preset: design-time data, trusted.
   */
  loadFromUnits(units: DeployedUnit[]): void {
    // Reset state (palette counters too, regardless of kind)
    this.placed = [];
    this.selectedPalette = -1;
    this.selectedUnit = -1;
    this.pathMode = false;
    this.dragging = false;
    this.dragType = '';
    this.dragPos = null;
    this.dragRemoveIdx = -1;
    for (const slot of this.palette) slot.placed = 0;

    const isManual = this.rules.kind === 'manual';
    for (const u of units) {
      const slot = this.palette.find(s => s.type === u.type);
      if (isManual) {
        // Manual mode: enforce cap. Unknown types (no slot) rejected too.
        if (!slot) continue;
        if (slot.placed >= slot.max) continue;
      }
      this.placed.push({
        type: u.type,
        pos: { x: u.pos.x, y: u.pos.y },
        path: u.path.map(p => ({ x: p.x, y: p.y })),
      });
      // Bump palette counter when applicable. In preset mode there's no
      // palette so `slot` is always undefined → this is a no-op.
      if (slot) slot.placed++;
    }
    this._emit();
  }

  /** Whether the player has placed at least 1 unit */
  canConfirm(): boolean {
    return this.placed.length > 0;
  }

  // ----------------------------------------------------------
  // Export to BattleUnit[]
  // ----------------------------------------------------------

  /**
   * Export `placed` as BattleUnit[] tagged with the given team.
   *
   * Single source of truth for ALL modes — single / PvP / story / casual
   * all share this method:
   *   - Single (single-player vs AI): team=0, viewFlipped=false. Caller
   *     merges with `generateAiUnits(rules.composition, rng)`.
   *   - PvP host: team=0, viewFlipped=false. Server merges with guest's.
   *   - PvP guest: team=1, viewFlipped=true (client stores in view coords
   *     which are bottom-of-screen = rows 6-7; convert to world before
   *     sending). Server merges with host's.
   *   - Story / Casual: team=0, viewFlipped=false. Caller merges with the
   *     level/chapter's preset AI units.
   *
   * `viewFlipped` rotates positions 180° (x→COLS-1-x, y→ROWS-1-y) so
   * guest's "my-side" view coords convert to world coords.
   *
   * PvP also gets team-prefixed unit IDs (T0U1, T0U2, ..., T1U1, ...) to
   * avoid collision when the server merges both sides (each client runs
   * its own `resetId()` so without the prefix both would start at U1).
   * Single / story / casual don't merge across clients so the prefix is
   * harmless cosmetic noise.
   */
  exportPlayerUnits(team: 0 | 1, viewFlipped: boolean = false): BattleUnit[] {
    const fx = (x: number) => viewFlipped ? BATTLE_COLS - 1 - x : x;
    const fy = (y: number) => viewFlipped ? BATTLE_ROWS - 1 - y : y;
    const units: BattleUnit[] = [];
    let idx = 0;
    for (const u of this.placed) {
      const cfg = UCFG[u.type];
      const wx = fx(u.pos.x), wy = fy(u.pos.y);
      const wPath = u.path.map((p) => ({ x: fx(p.x), y: fy(p.y) }));
      const unit = mkUnit(u.type, team, wx, wy, cfg.hp, wPath);
      idx += 1;
      unit.id = 'T' + team + 'U' + idx;
      units.push(unit);
    }
    return units;
  }
}

// ============================================================
// AI 對手生成（single mode 用）
// ============================================================

/**
 * Generate AI opponent units for single-player mode.
 *
 * Free function (not a Deployment method) because the AI doesn't share
 * Deployment's `placed` / palette state — it builds an independent unit
 * list keyed off the active manual rule's `composition`. Story / Casual
 * don't use this (they provide explicit AI units in the level data).
 *
 * Algorithm: for each (type, count) in composition, place `count` units
 * at random valid AI cells (rows AI_ROW_RANGE, excluding bases, no
 * stacking), each with a simple forward (y+) path of length
 * `ri(AI_MIN_PATH_STEPS, cfg.maxPath)`. Up to AI_MAX_PLACEMENT_ATTEMPTS
 * retries per unit if the random cell is occupied; gives up silently
 * if the map is too crowded.
 *
 * @param composition  [type, count] pairs (typically `rules.composition`
 *                     so AI mirrors player composition in single mode).
 * @param rng          Deterministic RNG. When null, falls back to
 *                     Math.random-backed `ri()` (legacy paths only).
 */
export function generateAiUnits(
  composition: ReadonlyArray<readonly [string, number]>,
  rng: SeededRng | null = null,
): BattleUnit[] {
  const rollInt = (a: number, b: number): number => rng ? rng.nextInt(a, b) : ri(a, b);
  const occupied = new Set<string>();
  const out: BattleUnit[] = [];

  for (const [type, count] of composition) {
    for (let i = 0; i < count; i++) {
      let attempts = 0;
      while (attempts < AI_MAX_PLACEMENT_ATTEMPTS) {
        const x = rollInt(0, BATTLE_COLS - 1);
        const y = rollInt(AI_MIN_ROW, AI_MAX_ROW);
        const key = `${x},${y}`;
        if (!BASE_TILES.has(key) && !occupied.has(key)) {
          occupied.add(key);
          const cfg = UCFG[type];
          const path: Pos[] = [];
          let cy = y;
          const maxSteps = Math.min(cfg.maxPath, rollInt(AI_MIN_PATH_STEPS, cfg.maxPath));
          for (let s = 0; s < maxSteps; s++) {
            cy++;
            if (cy >= BATTLE_ROWS) break;
            if (BASE_TILES.has(`${x},${cy}`)) break;
            path.push({ x, y: cy });
          }
          out.push(mkUnit(type, 1, x, y, cfg.hp, path));
          break;
        }
        attempts++;
      }
    }
  }
  return out;
}
